import React from "react";
import { CheckCircle2 } from "lucide-react";
import { Badge } from "components/Graphs/Badge";
import { Button } from "components/Graphs/Button";
import type { BuilderSection, SectionStatus } from "./types";

export const LaunchableCreatorPageContainer: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <div className="relative w-full overflow-hidden">
    <div className="relative z-10 mx-auto max-w-7xl py-10 sm:px-6 lg:px-8">
      <div className={`mx-auto px-4 ${className}`}>{children}</div>
    </div>
  </div>
);

interface SectionShellProps {
  id: BuilderSection;
  title: string;
  summary?: string;
  detailItems?: Array<{ label: string; value: string }>;
  collapsedPreview?: React.ReactNode;
  icon: React.ReactNode;
  status: SectionStatus;
  showCompletionIcon: boolean;
  canContinue: boolean;
  onNext: () => void;
  activeSection: BuilderSection | null;
  hasBeenOpened: boolean;
  setActiveSection: (section: BuilderSection | null) => void;
  children: React.ReactNode;
}

export const SectionIcon: React.FC<{
  icon: React.ReactNode;
  complete?: boolean;
  className?: string;
}> = ({ icon, complete = false, className = "" }) => (
  <div
    className={`${className} flex h-9 w-9 shrink-0 items-center justify-center rounded-md border ${
      complete
        ? "border-highlight bg-highlight/10 text-highlight"
        : "border-zinc-500 bg-zinc-500/10 text-zinc-500"
    }`}
  >
    {complete ? <CheckCircle2 className="h-5 w-5" /> : icon}
  </div>
);

export const SectionShell: React.FC<SectionShellProps> = ({
  id,
  title,
  summary,
  detailItems = [],
  collapsedPreview,
  icon,
  status,
  showCompletionIcon,
  canContinue,
  onNext,
  activeSection,
  hasBeenOpened,
  setActiveSection,
  children,
}) => {
  const isActive = activeSection === id;
  const hasSupportingContent =
    !isActive && Boolean(summary || detailItems.length > 0 || collapsedPreview);

  return (
    <section
      className={`rounded-md border bg-primaryBg transition-colors ${
        isActive ? "border-highlight" : "group cursor-pointer border-zinc-800"
      }`}
      onClick={() => {
        if (!isActive) {
          setActiveSection(id);
        }
      }}
    >
      <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
        <div
          className={`flex min-w-0 flex-1 gap-3 ${
            hasSupportingContent ? "items-start" : "items-center"
          }`}
        >
          <SectionIcon
            icon={icon}
            complete={showCompletionIcon}
            className={hasSupportingContent ? "mt-0.5" : ""}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-primary">{title}</h2>
              {status === "required" && (
                <Badge variant="neutral">Required</Badge>
              )}
              {!isActive && status === "incomplete" && (
                <Badge variant="error">Incomplete</Badge>
              )}
              {status === "error" && <Badge variant="error">Error</Badge>}
            </div>
            {!isActive && (
              <>
                {summary && (
                  <p className="mt-1 break-words text-sm text-secondary">
                    {summary}
                  </p>
                )}
                {detailItems.length > 0 && (
                  <dl className="mt-2 space-y-1 text-sm">
                    {detailItems.map((item) => (
                      <div
                        key={item.label}
                        className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2"
                      >
                        <dt className="font-medium text-secondary">
                          {item.label}:
                        </dt>
                        <dd className="break-words text-primary">
                          {item.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
                {collapsedPreview}
              </>
            )}
          </div>
        </div>
        {!isActive && (
          <Button
            type="button"
            variant="ghost"
            className="h-9 shrink-0 border border-gray-200 group-hover:bg-gray-200 hover:bg-gray-200 dark:border-zinc-700 dark:group-hover:bg-zinc-700 dark:hover:bg-zinc-700 md:border-transparent md:dark:border-transparent"
            aria-expanded={false}
            onClick={(event) => {
              event.stopPropagation();
              setActiveSection(id);
            }}
          >
            Edit
          </Button>
        )}
      </div>
      {hasBeenOpened && (
        <div hidden={!isActive} className="border-t border-zinc-800 p-4">
          {children}
          <div className="mt-6 flex justify-end">
            <Button
              type="button"
              className="disabled:text-zinc-600 dark:disabled:text-zinc-300"
              disabled={!canContinue}
              onClick={(event) => {
                event.stopPropagation();
                onNext();
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};
